import { FC } from "react";
import { useLanguage } from "../../context/LanguageContext";
import Signup from "../../assets/signup.png";
import Settings from "../../assets/settings.png";
import Plugin from "../../assets/plugin.png";
import AddingContent from "../../assets/contentful-adding-content.png";
import RequiredContent from "../../assets/contentful-required.png";
import ContentfulSignup from "../../assets/contentful-signup.png";
import ContentfulApi from "../../assets/contentful-api.png";
import ContentfulCreateSpace from "../../assets/contentful-create-space.png";
import { BreadCrumbHeading, LandingHeader, LandingSection } from "../../compositions/LandingPage";
import Video from "../../components/Video";
import styles from "./index.module.css";

const PreContent: FC = () => {
  const { t } = useLanguage();
  return (
    <>
      <LandingSection>
        <mui-v-stack space="var(--space-100)">
          <mui-heading level="4" size="5">
            {t("guides.customise-page.prerequisites.item-1.title")}
          </mui-heading>
          <mui-body size="small">{t("guides.customise-page.prerequisites.item-1.description")}</mui-body>
        </mui-v-stack>
        <img className={styles.image} src={Plugin} alt="Figma Guru Guides Plugin" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-100)">
          <mui-heading level="4" size="5">
            {t("guides.customise-page.prerequisites.item-2.title")}
          </mui-heading>
          <mui-body size="small">
            {t("guides.customise-page.prerequisites.item-2.description-start")}{" "}
            <mui-link size="small" href="https://www.contentful.com/sign-up/" target="_blank">
              {t("guides.customise-page.prerequisites.item-2.description-link")}
            </mui-link>{" "}
            {t("guides.customise-page.prerequisites.item-2.description-end")}
          </mui-body>
        </mui-v-stack>
        <img className={styles.image} src={ContentfulSignup} alt="Sign up" />
      </LandingSection>
    </>
  );
};

const EndContent: FC = () => {
  const { t } = useLanguage();
  return (
    <>
      <LandingSection>
        <mui-v-stack space="var(--space-100)">
          <mui-heading level="4" size="5">
            {t("guides.customise-page.step-5.item-1.title")}
          </mui-heading>

          <mui-body size="small">{t("guides.customise-page.step-5.item-1.description")}</mui-body>
        </mui-v-stack>
        <img className={styles.imageAlt} src={AddingContent} alt="Adding content in Contentful" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-100)">
          <mui-heading level="4" size="5">
            {t("guides.customise-page.step-5.item-2.title")}
          </mui-heading>

          <mui-body size="small">{t("guides.customise-page.step-5.item-2.description")}</mui-body>
        </mui-v-stack>

        <img className={styles.imageAlt} src={RequiredContent} alt="Hiding content types in Contentful" />
      </LandingSection>
    </>
  );
};

const CustomContent: FC = () => {
  const { t } = useLanguage();
  return (
    <>
      <mui-v-stack space="var(--space-500)">
        <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
          {t("guides.customise-page.prerequisites.title")}
        </mui-heading>

        <mui-responsive breakpoint="900">
          <mui-v-stack slot="showBelow" space="var(--space-600)">
            <PreContent />
          </mui-v-stack>
          <mui-grid slot="showAbove" col="1fr 1fr" space="var(--space-700)">
            <PreContent />
          </mui-grid>
        </mui-responsive>
      </mui-v-stack>

      <LandingSection>
        <mui-v-stack space="var(--space-200)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-0.title")}
          </mui-heading>
          <mui-body style={{ maxWidth: "75ch" }}>{t("guides.customise-page.step-0.description")}</mui-body>
        </mui-v-stack>
        <Video videoId="YoKhzXeyPOA?si=tnIrzi_JLi8k0QFH" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-200)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-1.title")}
          </mui-heading>
          <mui-body style={{ maxWidth: "75ch" }}>{t("guides.customise-page.step-1.description")}</mui-body>
        </mui-v-stack>
        <img className={styles.image} src={Signup} alt="Sign up" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-200)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-2.title")}
          </mui-heading>

          <mui-body style={{ maxWidth: "75ch" }}>{t("guides.customise-page.step-2.description")}</mui-body>
        </mui-v-stack>
        <img className={styles.image} src={ContentfulCreateSpace} alt="Create Space" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-200)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-3.title")}
          </mui-heading>
          <mui-list as="ol" style={{ maxWidth: "75ch" }}>
            <mui-list-item style={{ marginTop: "var(--space-300)" }}>
              {t("guides.customise-page.step-3.item-1")}
            </mui-list-item>
            <mui-list-item style={{ marginTop: "var(--space-300)" }}>
              {t("guides.customise-page.step-3.item-2")}
            </mui-list-item>
          </mui-list>
        </mui-v-stack>
        <img className={styles.image} src={ContentfulApi} alt="Sign up" />
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-300)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-4.title")}
          </mui-heading>
        </mui-v-stack>

        <mui-v-stack space="var(--space-800)" style={{ marginTop: 24 }}>
          <mui-v-stack space="var(--space-300)">
            <mui-heading level="3" size="5">
              {t("guides.customise-page.step-4.prompt-1.title")}
            </mui-heading>

            <mui-list as="ul">
              <mui-list-item size="small">{t("guides.customise-page.step-4.prompt-1.item-1")}</mui-list-item>
              <mui-list-item size="small">{t("guides.customise-page.step-4.prompt-1.item-2")}</mui-list-item>
            </mui-list>

            <mui-code class={styles.code}>contentful login</mui-code>
          </mui-v-stack>

          <mui-v-stack space="var(--space-300)">
            <mui-heading level="3" size="5">
              {t("guides.customise-page.step-4.prompt-2.title")}
            </mui-heading>

            <mui-list as="ul">
              <mui-list-item size="small">{t("guides.customise-page.step-4.prompt-2.item-1")}</mui-list-item>
              <mui-list-item size="small">{t("guides.customise-page.step-4.prompt-2.item-2")}</mui-list-item>
            </mui-list>

            <mui-code class={styles.code}>
              {
                "contentful space import --space-id YOUR_SPACE_ID --environment-id master --content-file https://raw.githubusercontent.com/guru-app-team/guru-guides-template/main/guru-guides-template.json"
              }
            </mui-code>
          </mui-v-stack>
        </mui-v-stack>
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-500)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-5.title")}
          </mui-heading>

          <mui-responsive breakpoint="900">
            <mui-v-stack slot="showBelow" space="var(--space-600)">
              <EndContent />
            </mui-v-stack>
            <mui-grid slot="showAbove" space="var(--space-700)" col="1fr 1fr">
              <EndContent />
            </mui-grid>
          </mui-responsive>
        </mui-v-stack>
      </LandingSection>

      <LandingSection>
        <mui-v-stack space="var(--space-200)">
          <mui-heading level="2" size="3" style={{ "--heading-text-color": "var(--theme-selected)" }}>
            {t("guides.customise-page.step-6.title")}
          </mui-heading>
          <mui-body>{t("guides.customise-page.step-6.description")}</mui-body>
        </mui-v-stack>
        <img className={styles.image} src={Settings} alt="Sign up" />
      </LandingSection>
    </>
  );
};

export const Customise: FC<{}> = () => {
  const { t } = useLanguage();
  return (
    <>
      <BreadCrumbHeading link={t("guides.title")} to="/guides" page={t("guides.customise-page.title")} />

      <LandingHeader>
        <mui-heading level="1">{t("guides.customise-page.title")}</mui-heading>

        <mui-body size="large" style={{ maxWidth: "65ch" }}>
          {t("guides.customise-page.description")}
        </mui-body>
      </LandingHeader>

      <mui-responsive breakpoint="900">
        <mui-v-stack slot="showBelow" space="var(--space-700)" style={{ marginBottom: "var(--space-600)" }}>
          <CustomContent />
        </mui-v-stack>
        <mui-v-stack slot="showAbove" space="calc(var(--space-800) * 2)" style={{ marginBottom: "var(--space-800)" }}>
          <CustomContent />
        </mui-v-stack>
      </mui-responsive>
    </>
  );
};

export default Customise;
